<script>
    import { parseMarkdown } from '../utils/markdown.js';
    import { formatForDisplay, isJsonLike, validateMessageContent } from '../utils/contentFormatter.js';
    
    export let message;
    export let isLast = false;
    
    $: isUser = message.type === 'user';
    $: isAI = message.type === 'ai';
    $: {
        console.log('ChatMessage received message:', message);
        console.log('Message content:', message.content);
        console.log('Message content type:', typeof message.content);
    }
    
    // Enhanced content processing using utility functions
    $: contentString = validateMessageContent(message.content);
    $: displayContent = formatForDisplay(contentString, isAI);
    $: parsedContent = isAI ? parseMarkdown(displayContent) : displayContent;
    $: isJsonContent = isJsonLike(message.content);
    
    // Format timestamp
    function formatTime(timestamp) {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('de-DE', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    }
    
    // Copy message content to clipboard
    async function copyMessage() {
        try {
            await navigator.clipboard.writeText(message.content);
        } catch (err) {
            console.error('Failed to copy message:', err);
        }
    }
</script>

<div class="message-container {isUser ? 'user' : 'ai'}" class:last={isLast}>
    <div class="message-bubble">
        <div class="message-header">
            <span class="message-sender">
                {isUser ? 'Du' : 'KI'}
            </span>
            <span class="message-time">
                {formatTime(message.timestamp)}
            </span>
        </div>
        
        <div class="message-content">
            {#if isAI}
                {@html parsedContent}
            {:else}
                {contentString}
            {/if}
        </div>
        
        <button 
            class="copy-button" 
            on:click={copyMessage}
            title="Nachricht kopieren"
            aria-label="Nachricht kopieren"
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
        </button>
    </div>
</div>

<style>
    .message-container {
        display: flex;
        margin-bottom: 16px;
        animation: fadeIn 0.3s ease-out;
    }
    
    .message-container.user {
        justify-content: flex-end;
    }
    
    .message-container.ai {
        justify-content: flex-start;
    }
    
    .message-container.last {
        margin-bottom: 24px;
    }
    
    .message-bubble {
        position: relative;
        max-width: 70%;
        min-width: 120px;
        padding: 12px 16px;
        border-radius: 18px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        word-wrap: break-word;
        transition: all 0.2s ease;
    }
    
    .message-bubble:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    
    .message-bubble:hover .copy-button {
        opacity: 1;
    }
    
    .user .message-bubble {
        background: linear-gradient(135deg, #4a9eff 0%, #0066cc 100%);
        color: white;
        border-bottom-right-radius: 4px;
    }
    
    .ai .message-bubble {
        background: #2d2d2d;
        color: #ffffff;
        border: 1px solid #404040;
        border-bottom-left-radius: 4px;
    }
    
    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
        font-size: 0.75rem;
        opacity: 0.8;
    }
    
    .message-sender {
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .message-time {
        font-size: 0.7rem;
        opacity: 0.7;
    }
    
    .message-content {
        line-height: 1.5;
        font-size: 0.95rem;
    }
    
    /* Markdown styling within messages */
    .message-content :global(pre) {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 6px;
        padding: 12px;
        margin: 8px 0;
        overflow-x: auto;
        font-size: 0.85rem;
    }
    
    .user .message-content :global(pre) {
        background: rgba(255, 255, 255, 0.15);
    }
    
    .message-content :global(code) {
        background: rgba(0, 0, 0, 0.2);
        padding: 2px 4px;
        border-radius: 3px;
        font-size: 0.9em;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    }
    
    .user .message-content :global(code) {
        background: rgba(255, 255, 255, 0.2);
    }
    
    .message-content :global(ul),
    .message-content :global(ol) {
        margin: 8px 0;
        padding-left: 20px;
    }
    
    .message-content :global(li) {
        margin: 4px 0;
    }
    
    .message-content :global(strong) {
        font-weight: 600;
    }
    
    .message-content :global(em) {
        font-style: italic;
    }
    
    .message-content :global(a) {
        color: inherit;
        text-decoration: underline;
        opacity: 0.9;
    }
    
    .message-content :global(a:hover) {
        opacity: 1;
    }
    
    .copy-button {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.1);
        border: none;
        border-radius: 4px;
        padding: 4px;
        cursor: pointer;
        opacity: 0;
        transition: all 0.2s ease;
        color: inherit;
    }
    
    .copy-button:hover {
        background: rgba(0, 0, 0, 0.2);
        transform: scale(1.1);
    }
    
    .copy-button:active {
        transform: scale(0.95);
    }
    
    @media (max-width: 768px) {
        .message-bubble {
            max-width: 85%;
            padding: 10px 14px;
        }
        
        .copy-button {
            opacity: 1;
        }
    }
    
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>
